import type { CountryCodesAbbreviationsType } from '@axiom/const';
import {
  AddressStateConst,
  AreasOfStudyCategoriesConst,
  CountryCodes,
  states,
} from '@axiom/const';
import type { BarredLocation } from '@axiom/validation';

import { PARALEGAL } from '../../api/constants';

type AddressStateOption = { label: string; value: string | string[] };

export const CandidateFiltersFormUtil = {
  getAddressCountries() {
    return Object.entries(CountryCodes)
      .filter(([key]) =>
        ['AU', 'CA', 'DE', 'HK', 'SG', 'CH', 'UK', 'US'].includes(key)
      )
      .map(([value, label]) => ({ value, label }));
  },

  getUsStateOptions(): AddressStateOption[] {
    const timezoneGroupOptions: AddressStateOption[] = Object.entries(
      AddressStateConst.usTimezoneStateGroups
    ).map(([label, groupStates]) => ({ label, value: [...groupStates] }));

    const stateOptions: AddressStateOption[] = Object.entries(states).map(
      ([value, label]) => ({ value, label })
    );

    return [...timezoneGroupOptions, ...stateOptions];
  },

  getAustraliaStateOptions(): AddressStateOption[] {
    return AddressStateConst.australiaStatesAndTerritories.map(name => ({
      value: name,
      label: name,
    }));
  },

  getCanadaProvinceOptions(): AddressStateOption[] {
    return AddressStateConst.canadaProvincesAndTerritories.map(name => ({
      value: name,
      label: name,
    }));
  },

  getStateDropdownConfig(countryCode?: CountryCodesAbbreviationsType | null) {
    switch (countryCode) {
      case 'US': {
        return {
          label: 'Address state',
          options: CandidateFiltersFormUtil.getUsStateOptions(),
        };
      }
      case 'AU': {
        return {
          label: 'Address State or Territory',
          options: CandidateFiltersFormUtil.getAustraliaStateOptions(),
        };
      }
      case 'CA': {
        return {
          label: 'Address Province or Territory',
          options: CandidateFiltersFormUtil.getCanadaProvinceOptions(),
        };
      }
      default: {
        return null;
      }
    }
  },

  getAreaOfStudyCategoryOptions() {
    const stemCategories = [
      'AppliedScience',
      'FormalScience',
      'NaturalScience',
      'SocialScience',
      'BusinessGovernmentEducationAndMedia',
    ];

    const options: Array<{ label: string; value: string[] }> = Object.entries(
      AreasOfStudyCategoriesConst.Categories
    ).map(([value, label]) => ({ value: [value], label }));

    options.unshift({ label: 'All STEM', value: stemCategories });

    return options;
  },

  getBarredLocationOptions(barredLocations: BarredLocation[]) {
    const options = barredLocations.map(bl => ({
      label: bl.name,
      value: bl.id,
    }));
    // Add paralegal option
    options.push({ label: PARALEGAL, value: PARALEGAL });

    return options.sort(({ label: nameA }, { label: nameB }) => {
      if (nameA && nameB) {
        const textA = nameA.toUpperCase();
        const textB = nameB.toUpperCase();
        if (textA < textB) return -1;
        if (textA > textB) return 1;
      }
      return 0;
    });
  },

  getInitialRollOffDatePickerValue(daysToSoonestEngagementEnd?: number | null) {
    return !daysToSoonestEngagementEnd || daysToSoonestEngagementEnd < 0
      ? 0
      : daysToSoonestEngagementEnd;
  },

  getInitialRollOffDateDropDownValue(
    daysToSoonestEngagementEnd?: number | null
  ) {
    if (!daysToSoonestEngagementEnd && daysToSoonestEngagementEnd !== 0) {
      return null;
    }
    return [null, 15, 30, 45].includes(daysToSoonestEngagementEnd)
      ? daysToSoonestEngagementEnd
      : 0;
  },
};
